import type { BillingFrequency, OrderFormLine, OrderFormLineDiscountType, OrderFormLinePricing } from '../types'
import { number, signedUnitMoney, unitMoney } from './format'
import { cents } from './proposal-math'

/**
 * Per-line money for order forms: the normal price, the line discount, the
 * net price, flat fees and billing frequency. The editor, the HTML document
 * and the PDF all call these, so they always agree.
 *
 * Term math. The order form total is the amount for the whole term.
 * - One-Time lines count once.
 * - Per Unit lines billed Annually count once: their quantity already covers
 *   the term (the pooled-licenses convention, 2 years of 1,000 licenses is
 *   one line of 2,000). Every line saved before billing frequency existed
 *   reads this way, so old forms keep their totals.
 * - Monthly and Quarterly lines repeat for each period in the term.
 * - Flat Fee lines billed Annually repeat for each year in the term.
 * The term comes from the form's dates. Without dates, one year is assumed
 * and `termAssumed` says so.
 */

export const BILLING_FREQUENCIES: Array<{ key: BillingFrequency; label: string }> = [
  { key: 'one_time', label: 'One-Time' },
  { key: 'monthly', label: 'Monthly' },
  { key: 'quarterly', label: 'Quarterly' },
  { key: 'annually', label: 'Annually' },
]
export const RECURRING_FREQUENCIES = BILLING_FREQUENCIES.filter((entry) => entry.key !== 'one_time')
export const LINE_PRICING_LABEL: Record<OrderFormLinePricing, string> = { per_unit: 'Per Unit', flat: 'Flat Fee' }

/** "/month" style suffix for a price with a stated frequency. */
export const FREQUENCY_SUFFIX: Record<BillingFrequency, string> = { one_time: ' one-time', monthly: '/month', quarterly: '/quarter', annually: '/year' }

/** A Stripe product id, as an admin may pick one for a line. */
export const STRIPE_PRODUCT_ID = /^prod_[A-Za-z0-9]{1,64}$/

const CATEGORIES: OrderFormLine['category'][] = ['license', 'add_on', 'one_time']
const FREQUENCIES: BillingFrequency[] = ['one_time', 'monthly', 'quarterly', 'annually']

/** Removes floating-point noise (3 x 0.4 = 1.2000000000000002) without touching real fractions of a cent such as $4.375. */
function snap(value: number): number {
  return Math.round(value * 1e9) / 1e9
}

export function linePricing(line: Pick<OrderFormLine, 'pricing'>): OrderFormLinePricing {
  return line.pricing === 'flat' ? 'flat' : 'per_unit'
}

export function lineFrequency(line: Pick<OrderFormLine, 'category' | 'billingFrequency'>): BillingFrequency {
  if (line.category === 'one_time') return 'one_time'
  return line.billingFrequency && line.billingFrequency !== 'one_time' && FREQUENCIES.includes(line.billingFrequency) ? line.billingFrequency : 'annually'
}

type DiscountLine = Pick<OrderFormLine, 'unitPrice' | 'discountType' | 'discountValue'>

/** True when the line carries a discount that changes its price. Credits and $0 lines never do. */
export function lineHasDiscount(line: DiscountLine): boolean {
  const value = line.discountValue
  return line.unitPrice !== null && Number.isFinite(line.unitPrice) && line.unitPrice > 0
    && (line.discountType === 'percent' || line.discountType === 'amount')
    && typeof value === 'number' && Number.isFinite(value) && value > 0
}

/** The price after the line discount, never below 0. Null when no price is entered. */
export function lineNetPrice(line: DiscountLine): number | null {
  if (line.unitPrice === null || !Number.isFinite(line.unitPrice)) return null
  if (!lineHasDiscount(line)) return line.unitPrice
  const value = line.discountValue as number
  const net = line.discountType === 'percent' ? line.unitPrice * (1 - Math.min(value, 100) / 100) : line.unitPrice - value
  return Math.max(0, snap(net))
}

/** True when the discount takes the whole price: the line reads "Included". */
export function lineIncluded(line: DiscountLine): boolean {
  return lineHasDiscount(line) && lineNetPrice(line) === 0
}

/** "60% off", "$2 off", or "Included". Empty when there is no discount. */
export function lineDiscountText(line: DiscountLine): string {
  if (!lineHasDiscount(line)) return ''
  if (lineIncluded(line)) return 'Included'
  const value = line.discountValue as number
  return line.discountType === 'percent' ? `${snap(Math.min(value, 100))}% off` : `${unitMoney(value)} off`
}

export interface LinePeriods {
  /** How many times the line repeats in the term. 1.5 for a flat yearly fee on an 18-month term. */
  count: number
  unit: 'month' | 'quarter' | 'year'
  /** True when the form has no term dates, so one year was assumed. */
  termAssumed: boolean
}

/** How often a line repeats over the term, or null when it counts once (one-time and per-unit annual lines). */
export function linePeriods(line: Pick<OrderFormLine, 'category' | 'billingFrequency' | 'pricing'>, termMonths: number | null): LinePeriods | null {
  const frequency = lineFrequency(line)
  if (frequency === 'one_time') return null
  if (frequency === 'annually' && linePricing(line) === 'per_unit') return null
  const months = termMonths !== null && termMonths > 0 ? termMonths : 12
  const termAssumed = !(termMonths !== null && termMonths > 0)
  if (frequency === 'monthly') return { count: months, unit: 'month', termAssumed }
  if (frequency === 'quarterly') return { count: snap(months / 3), unit: 'quarter', termAssumed }
  return { count: snap(months / 12), unit: 'year', termAssumed }
}

/** The periods worth showing to a reader: null when the line counts once (one-time, per-unit annual, or exactly one period). */
export function lineRepeats(line: Pick<OrderFormLine, 'category' | 'billingFrequency' | 'pricing'>, termMonths: number | null): LinePeriods | null {
  const periods = linePeriods(line, termMonths)
  return periods && periods.count !== 1 ? periods : null
}

export function lineMultiplier(line: Pick<OrderFormLine, 'category' | 'billingFrequency' | 'pricing'>, termMonths: number | null): number {
  return linePeriods(line, termMonths)?.count ?? 1
}

/** "12 months", "4 quarters", "1 year", "1.5 years". */
export function periodsText(periods: LinePeriods): string {
  const count = Number.isInteger(periods.count) ? number(periods.count) : String(Math.round(periods.count * 100) / 100)
  return `${count} ${periods.unit}${periods.count === 1 ? '' : 's'}`
}

function baseAmount(line: OrderFormLine, price: number | null): number | null {
  if (price === null) return null
  if (linePricing(line) === 'flat') return price
  if (line.quantity === null || !Number.isFinite(line.quantity)) return null
  return line.quantity * price
}

/** The line's amount for the whole term at the net price. Null until the price (and, per unit, the quantity) are entered. */
export function orderFormLineAmount(line: OrderFormLine, termMonths: number | null): number | null {
  const base = baseAmount(line, lineNetPrice(line))
  return base === null ? null : cents(base * lineMultiplier(line, termMonths))
}

/** The line's amount for the whole term before the line discount (the struck-through figure). */
export function orderFormLineGrossAmount(line: OrderFormLine, termMonths: number | null): number | null {
  const base = baseAmount(line, line.unitPrice !== null && Number.isFinite(line.unitPrice) ? line.unitPrice : null)
  return base === null ? null : cents(base * lineMultiplier(line, termMonths))
}

export function sumOrderFormLines(lines: OrderFormLine[], termMonths: number | null): number {
  return cents(lines.reduce((sum, line) => sum + (orderFormLineAmount(line, termMonths) ?? 0), 0))
}

/** The rate suffix the customer reads after a price: "/month", "/quarter", or "/year" on a flat yearly fee. */
export function lineRateSuffix(line: OrderFormLine): string {
  const frequency = lineFrequency(line)
  if (frequency === 'monthly' || frequency === 'quarterly') return FREQUENCY_SUFFIX[frequency]
  if (frequency === 'annually' && linePricing(line) === 'flat' && !/year|annual/i.test(line.unitLabel || '')) return FREQUENCY_SUFFIX.annually
  return ''
}

/** A price as the customer reads it: "$3/license", "$99/month", "-$22,039". */
export function linePriceText(line: OrderFormLine, price: number): string {
  const unit = linePricing(line) === 'flat' ? '' : line.unitLabel || ''
  return `${signedUnitMoney(price)}${unit}${lineRateSuffix(line)}`
}

/** The quiet math under an editor line total: "1,500 × $3, 60% off", "$99 × 12 months". Empty when there is nothing to explain. */
export function lineBreakdown(line: OrderFormLine, termMonths: number | null): string {
  if (line.unitPrice === null) return ''
  const flat = linePricing(line) === 'flat'
  if (!flat && line.quantity === null) return ''
  // A line that repeats once over the term ("× 1 year") needs no period in the math.
  const periods = lineRepeats(line, termMonths)
  // The math reads "100% off" here; the customer document says "Included".
  const value = line.discountValue as number
  const discount = !lineHasDiscount(line) ? '' : line.discountType === 'percent' ? `${snap(Math.min(value, 100))}% off` : `${unitMoney(value)} off`
  const parts = [flat ? signedUnitMoney(line.unitPrice) : `${number(line.quantity!)} × ${signedUnitMoney(line.unitPrice)}`]
  if (periods) parts[0] += ` × ${periodsText(periods)}`
  const showMath = (!flat && line.quantity !== 1) || periods !== null || discount !== ''
  if (!showMath) return ''
  return discount ? `${parts[0]}, ${discount}` : parts[0]
}

/** True when a line repeats over a term the form has not set yet. */
export function termAssumedFor(lines: OrderFormLine[], termMonths: number | null): boolean {
  return lines.some((line) => linePeriods(line, termMonths)?.termAssumed === true)
}

/**
 * Rebuilds one stored line from explicit field checks, in the current shape.
 * A line saved before line discounts existed (unitPrice was the price paid,
 * listPrice the struck-through price) becomes price = listPrice with the
 * discount that gives the same net: 100% off when it was $0, otherwise
 * dollars off per unit. Totals do not change.
 */
export function normalizeOrderFormLine(raw: unknown): OrderFormLine | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const item = raw as Record<string, unknown>
  if (typeof item.id !== 'string') return null
  const str = (v: unknown): string => typeof v === 'string' ? v : ''
  const num = (v: unknown): number | null => typeof v === 'number' && Number.isFinite(v) ? v : null
  const category = CATEGORIES.includes(item.category as OrderFormLine['category']) ? item.category as OrderFormLine['category'] : 'add_on'
  const pricing: OrderFormLinePricing = item.pricing === 'flat' ? 'flat' : 'per_unit'
  const billingFrequency = lineFrequency({ category, billingFrequency: item.billingFrequency as BillingFrequency | undefined })
  let unitPrice = num(item.unitPrice)
  let discountType: OrderFormLineDiscountType | null = item.discountType === 'percent' || item.discountType === 'amount' ? item.discountType : null
  let discountValue = discountType ? num(item.discountValue) : null
  const listPrice = num(item.listPrice)
  const hasDiscount = discountValue !== null && discountValue > 0
  if (!hasDiscount && listPrice !== null && unitPrice !== null && unitPrice >= 0 && listPrice > unitPrice) {
    discountType = unitPrice === 0 ? 'percent' : 'amount'
    discountValue = unitPrice === 0 ? 100 : snap(listPrice - unitPrice)
    unitPrice = listPrice
  }
  return {
    id: item.id,
    category,
    name: str(item.name),
    description: str(item.description),
    quantity: num(item.quantity),
    unitPrice,
    unitLabel: str(item.unitLabel) || null,
    pricing,
    billingFrequency,
    discountType,
    discountValue,
    // An admin's Stripe product pick. Kept only when it looks like a product id.
    ...(typeof item.stripeProductId === 'string' && STRIPE_PRODUCT_ID.test(item.stripeProductId) ? { stripeProductId: item.stripeProductId } : {}),
  }
}
